// Types shaped to contracts/books-api.yaml admin schemas (frozen). CamelCase on the wire.

import type { ActivityBooking } from "./console/types.js";

/**
 * A company's internal key (`syn-rentals`). Never a word a person reads.
 *
 * SOFT brand (rd round 12, A4-1). A hard brand (`string & { readonly
 * __brand: "CompanySlug" }`) produces 681 type errors across the portal, which
 * no single lane can own, so the marker is optional: a plain `string` is still
 * assignable in both directions and nothing breaks. The brand exists so the
 * source guard (`rd-r12-a4-brand-guard.test.ts`) can tell a slug from any other
 * string by TYPE — annotation only, no field added, renamed or removed.
 */
export type CompanySlug = string & { readonly __companySlug?: never };

/**
 * A ledger account path (`Expenses:Repairs`). Never a word a person reads; the
 * console words it through `accountLabel` / `accountLabelAmong`. Soft brand for
 * the same reason as `CompanySlug` (rd round 12, A4-1).
 */
export type AccountPath = string & { readonly __accountPath?: never };

/**
 * A receipt's "Show details" text: the Worker's own sentence, raw ids, slugs
 * and paths. Never words a person reads on the card face (rd round 12, A4-3).
 *
 * Folded round-2 LOW 2. `ReceiptWords.details` was a plain `string`, so nothing
 * could see a page printing it as a face. The soft brand gives
 * `rd-r12-a4-brand-guard.test.ts` a marker: a `ReceiptDetails` expression is
 * allowed only inside a `disclosure(...)` body.
 */
export type ReceiptDetails = string & { readonly __receiptDetails?: never };

export type QueueReason =
  | "new-vendor"
  | "low-confidence"
  | "anomaly"
  | "possible-personal"
  | "unmatched-deposit"
  | "booking-failed"
  | "close-blocked"
  | "statement-blocked"
  | "escrow-balance"
  | "untracked-counterparty"
  | "receipt_card_not_attached";

export type QueueStatus = "pending" | "approved" | "edited" | "rejected";

/** Projection of raw_transaction_events.event_type (owner grant 2026-08-03). */
export type QueueEventType = "added" | "modified" | "removed";

/** Matched hub receipt surfaced on queue cards (QueueItem.receiptContext). */
export type QueueReceiptExpenseType =
  | "turnover"
  | "remodel"
  | "repair"
  | "gas"
  | "meals"
  | "gl_account";

export type QueueReceiptReviewFlag = "wrong_card" | "reimbursement" | "other";

export interface QueueReceiptContext {
  originKind: "mjn_ops_receipt";
  originId: string;
  matchState: "matched";
  /** Required tri-state. null = no claim; never treat as verified. */
  matchVerified: boolean | null;
  vendor: string;
  location: string;
  property: string;
  sorAddress?: string | null;
  expenseType: QueueReceiptExpenseType;
  subCategory?: string | null;
  /** Present iff expenseType is "gl_account": the hub account name as sent. */
  glAccount?: string;
  glParent?: string;
  capitalReplacement: boolean | null;
  reviewFlag: QueueReceiptReviewFlag | null;
  note?: string;
  submittedBy: string;
  imageUrl: string;
  receiptDate: string;
  amountDecimal: string;
  /** EA-C. Slug of the company whose charge matched, or null. */
  matchedCompany?: CompanySlug | null;
}

export interface QueueItem {
  id: number;
  company: CompanySlug;
  plaidTransactionId?: string;
  txnDate?: string;
  amount?: string;
  merchant?: string;
  descriptor?: string;
  /** GRANT 7 — stored Plaid original_description. Omit when null. */
  originalDescription?: string;
  /** GRANT 7 — stored Plaid payment_channel. Omit when null. */
  paymentChannel?: "online" | "in store" | "other";
  /** 2026-08-29 grant — stored payment_meta keys. Omit when empty. */
  paymentMeta?: {
    referenceNumber?: string;
    ppdId?: string;
    payee?: string;
    payer?: string;
    reason?: string;
  };
  counterparties?: Array<{
    name: string;
    type?: string;
    website?: string;
    entityId?: string;
    confidenceLevel?: "VERY_HIGH" | "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";
  }>;
  /** Plaid category list — not a CoA account. */
  plaidCategory?: string[];
  checkNumber?: string;
  /** Plaid location — not suggestedLocation / booking location. */
  plaidLocation?: {
    address?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
    lat?: number;
    lon?: number;
    storeNumber?: string;
  };
  reason: QueueReason;
  /** Read-only projection of raw_transaction_events.event_type. */
  eventType?: QueueEventType;
  suggestedAccount?: string;
  suggestedLocation?: string;
  confidence?: number;
  aiRationale?: string;
  notifiedAt?: string | null;
  status: QueueStatus;
  resolvedBy?: string;
  resolvedAccount?: string;
  resolvedLocation?: string;
  resolutionNote?: string;
  makeRule?: boolean;
  createdAt: string;
  resolvedAt?: string | null;
  /** Set when escalated to Owner; cleared when returned to AM. */
  escalatedAt?: string | null;
  escalatedBy?: string;
  escalationNote?: string;
  escalationClearedAt?: string | null;
  /** Blocker-shaped rows (close-blocked / statement-blocked / escrow-balance) — already on wire. */
  dueDate?: string;
  subject?: string;
  body?: string;
  /** Additive receipt context when a matched receipt exists for the txn. */
  receiptContext?: QueueReceiptContext;
  /** Owner grant 2026-09-02: stored AM-entered escrow event. Present ≠ cleared. */
  escrowDisbursementDate?: string;
  escrowDisbursementAmount?: string;
  escrowDisbursementKind?: EscrowDisbursementKind;
  /** GRANT G1: complete decision the AM was shown. Pending untracked only. */
  reviewedDecision?: QueueReviewedDecisionSnapshot;
}

/**
 * GL-35. Subject prefix of the close-blocked card a settled Moov wallet
 * movement raises when no rule books it. Mirrors the Worker's
 * MOOV_CARD_SUBJECT_PREFIX (ingest-worker/src/moov_wallet.ts).
 */
export const MOOV_CARD_SUBJECT_PREFIX = "Moov wallet unbooked \u2014 ";

/** Currently escalated = handed to Owner and not yet returned. */
export function isQueueItemEscalated(item: QueueItem): boolean {
  return (
    item.escalatedAt !== undefined &&
    item.escalatedAt !== null &&
    (item.escalationClearedAt === undefined || item.escalationClearedAt === null)
  );
}

export interface QueueEscalateRequest {
  note: string;
}

export interface QueueReturnToAmRequest {
  note?: string;
}

export interface QueueEscalateToPracticeRequest {
  note?: string;
}

export type QueueEscalateToPracticeResponse =
  | {
      company: CompanySlug;
      reviewQueueId: number;
      externalRef: string;
      questionType: "general";
      subject: string;
      body: string;
      dueDate: string;
      status: "open";
      replay: boolean;
    }
  | {
      company: CompanySlug;
      reviewQueueId: number;
      externalRef: string;
      questionType: "transaction";
      status: "open";
      replay: boolean;
    };

/** GRANT (16) — standing-rule width the AM chose. */
export type QueueRuleScope = "amount" | "vendor";

/** Owner grant 2026-08-24 — posting (Kind B) vs vendor-only identity (Kind A). */
export type QueueRuleKind = "posting" | "vendor_identity";

/** Owner grant 2026-08-25 — 1099 contractor vs non-1099 vendor. Omit = unclassified. */
export type QueueVendorClassification = "1099_contractor" | "non_1099_vendor";

/** Owner grant 2026-09-02 — closed §9 set. Underscore is the claim-key spelling. */
export type EscrowDisbursementKind = "taxes" | "insurance" | "mortgage_insurance";

/** GRANT G1 — same closed hub character set; not a new vocabulary. */
export type QueueReviewedDecisionCharacter =
  | "loan_to"
  | "loan_from"
  | "capital_contribution"
  | "owner_distribution"
  | "intercompany_transfer"
  | "loan_repayment";

export type QueueReviewedDecisionCharacterSource =
  | "hub_transaction_context"
  | "none";

export type UntrackedCounterpartyDisposition =
  | "mark-external"
  | "create-company"
  | "confirm-alias";

/** GET queue projection of the decision the AM is shown. */
export interface QueueReviewedDecisionSnapshot {
  company: CompanySlug;
  queueItemId: number;
  plaidTransactionId: string;
  label: string;
  merchant: string;
  descriptor?: string;
  amount?: string;
  txnDate?: string;
  fromLabel: string;
  toLabel: string;
  rationale?: string;
  character?: QueueReviewedDecisionCharacter;
  characterSource: QueueReviewedDecisionCharacterSource;
  contextOriginId?: string;
  contextAmount?: string;
  contextDate?: string;
}

/** Resolve body: snapshot plus the chosen disposition. */
export interface QueueResolutionReviewedDecision extends QueueReviewedDecisionSnapshot {
  disposition: UntrackedCounterpartyDisposition;
  targetSlug?: CompanySlug;
}

export interface QueueResolution {
  action: "approve" | "edit" | "reject";
  account?: AccountPath;
  location?: string;
  note?: string;
  makeRule?: boolean;
  /** GRANT (16). Omit = pin. Never default to vendor. */
  ruleScope?: QueueRuleScope;
  /** Owner grant 2026-08-24. Literal token span; omit = today's producer. */
  matchText?: string;
  /** Owner grant 2026-08-24. Omit = posting producer. */
  ruleKind?: QueueRuleKind;
  /** Owner grant 2026-08-25. Omit = unclassified. Never default. */
  vendorClassification?: QueueVendorClassification;
  /** Owner grant 2026-09-02. All three or none. action must be approve. */
  escrowDisbursementDate?: string;
  escrowDisbursementAmount?: string;
  escrowDisbursementKind?: EscrowDisbursementKind;
  /** GRANT G1. Required when resolving untracked-counterparty. */
  reviewedDecision?: QueueResolutionReviewedDecision;
  /**
   * GRANT G15 — owner 2026-11-02 (rd15 lane B). Answer a card by spreading its
   * bank line across several accounts and places. 2 to 20 parts; each amount
   * is the part's own share of the line (same sign as the line, Plaid
   * convention) and the parts must add up to it to the cent. Sent alone:
   * never with account, location or makeRule (a split is not a standing rule).
   */
  splits?: QueueSplitPart[];
}

/** One part of a split answer. The place rides on the ledger line itself. */
export interface QueueSplitPart {
  account: AccountPath;
  location: string;
  amount: string;
}

/** GET /v1/admin/{company}/chart-snapshot — GRANT (15). */
export interface ChartSnapshot {
  company: CompanySlug;
  present: boolean;
  pushedAt: string | null;
  accounts: AccountPath[];
  locations: string[];
  selectableAccounts: string[];
  selectableLocations: string[];
}

export function absentChartSnapshot(company: CompanySlug): ChartSnapshot {
  return {
    company,
    present: false,
    pushedAt: null,
    accounts: [],
    locations: [],
    selectableAccounts: [],
    selectableLocations: [],
  };
}

/** GET /v1/admin/{company}/prior-payments/candidates — grant 2026-08-26. */
export interface PriorPaymentCandidate {
  merchant: string | null;
  txnDate: string;
  amount: string;
  account: AccountPath;
  location: string;
  plaidTransactionId: string;
  ledgerPayee: string;
  attachedVendorId: string | null;
  cohortPlaidIds: string[];
}

export interface PriorPaymentCandidatesResponse {
  company: CompanySlug;
  period: string;
  candidates: PriorPaymentCandidate[];
}

export interface Rule {
  id: number;
  match: {
    name_regex?: string;
    merchant_regex?: string;
    account_last4?: string;
    amount?: string;
  };
  post: {
    account: string;
    location: string;
    payer?: string;
    flag?: "owner-review";
  };
  createdBy: string;
  createdAt: string;
  active: boolean;
  /**
   * Present on the rules LIST read only (rd r13 L6). `on` means an active
   * rule is in rules.yaml; `off` means it is listed and no longer applies.
   * A rule is never `on` because a card asked for it.
   */
  state?: "on" | "off";
  /** A turn-off is stored and not applied yet; the rule is still `on`. */
  turnOffRequested?: boolean;
  /** Where the rule came from. `cardId` is never rendered on a face. */
  source?: { kind: "card" | "hub" | "console" | "other"; cardId?: number };
}

/**
 * A card answer that asked for a standing rule and whose rule is not in
 * rules.yaml: waiting on the next run, or not added with the reason. Read
 * from the same list as the rules (rd r13 L6).
 */
export interface CardRuleAsk {
  cardId: number;
  payee: string;
  account: string;
  location: string;
  answeredAt: string;
  state: "waiting" | "not_added";
  reason: CardRuleNotAddedReason | null;
}

/**
 * Why the nightly run could not make the rule a card answer asked for. A
 * closed set from the pipeline; the page turns it into a sentence and never
 * shows the code (round-12 decision 7 / wording rule).
 */
export type CardRuleNotAddedReason =
  | "conflict"
  | "place"
  | "direction"
  | "name"
  | "text"
  | "vendor"
  | "not_booked"
  | "replaced"
  | "other";

export interface RuleChange {
  action: "append" | "deactivate";
  rule?: Rule;
  ruleId?: number;
}

/**
 * Wire/row status values for rule_change_requests. Does NOT include
 * `dismissed` — dismissal is a column pair; DB/wire status stays `failed`.
 * Runtime const so portal tests can pin the split (mirrors contracts N4).
 */
export const RULE_CHANGE_REQUEST_STATUSES = [
  "pending",
  "awaiting_approval",
  "rejected",
  "applied",
  "failed",
] as const;

export type RuleChangeRequestStatus =
  (typeof RULE_CHANGE_REQUEST_STATUSES)[number];

/**
 * List filter: row statuses plus `dismissed` (complete dismissal pair).
 * `dismissed` never appears on the row's `status` field.
 */
export const RULE_CHANGE_REQUEST_STATUS_FILTERS = [
  ...RULE_CHANGE_REQUEST_STATUSES,
  "dismissed",
] as const;

export type RuleChangeRequestStatusFilter =
  (typeof RULE_CHANGE_REQUEST_STATUS_FILTERS)[number];

export interface RuleChangeRequest {
  id: string;
  company: CompanySlug;
  action: "append" | "deactivate";
  payloadJson?: string;
  status: RuleChangeRequestStatus;
  requestedBy: string;
  createdAt: string;
  appliedAt?: string | null;
  lastError?: string | null;
  amDecidedBy?: string | null;
  amDecidedAt?: string | null;
  dismissedBy?: string | null;
  dismissedAt?: string | null;
  originKind?:
    | "admin_append"
    | "admin_deactivate"
    | "hub_question"
    | "queue_card"
    | null;
  originId?: string | null;
  justifyingTransaction?: {
    externalRef: string;
    txnDate?: string | null;
    amount?: string | null;
    direction?: "charge" | "deposit" | null;
  } | null;
}

export type LedgerChangeTarget = "account_mapping" | "coa_account" | "vocabulary";
export type CoaCharacter = "ordinary" | "loan" | "intercompany" | "equity";

export type LedgerChangePropose =
  | {
      action: "open";
      account: AccountPath;
      openDate: string;
      currencies: string[];
      character: CoaCharacter;
    }
  | { action: "close"; account: AccountPath; closeDate: string }
  | {
      action: "map";
      plaidAccountId: string;
      account: AccountPath;
      mask: string;
      statementCycleDay?: number | "unknown" | null;
    }
  | { action: "add_term"; key: "locations"; term: string };

export interface LedgerChangeRequest {
  id: string;
  company: CompanySlug;
  target: LedgerChangeTarget;
  action: "open" | "close" | "map" | "add_term";
  payloadJson?: string;
  status: RuleChangeRequestStatus;
  requestedBy: string;
  createdAt: string;
  appliedAt?: string | null;
  lastError?: string | null;
  amDecidedBy?: string | null;
  amDecidedAt?: string | null;
  originKind?: "am_propose" | null;
  originId?: string | null;
}
export interface CompanyHealth {
  company: CompanySlug;
  plaidItems: Array<{
    plaidItemId: string;
    institution?: string | null;
    status: "healthy" | "login_required" | "error" | "revoked";
    lastSyncedAt?: string | null;
  }>;
  lastRuns: Array<{
    runType: "plaid_sync" | "processing" | "reporting" | "notify" | "close" | "invariants";
    status: "running" | "succeeded" | "failed";
    startedAt: string;
    completedAt?: string | null;
    txnsProcessed?: number | null;
    queueAdded?: number | null;
    /** Present when the run failed; distinguishes finalize_failed from other failures. */
    errorCode?: string | null;
  }>;
}

export type CompanyType = "gv-internal" | "gvpm-practice" | "external";
export type CompanyBasis = "cash" | "accrual";
export type CompanyFeaturePack = "core" | "medical";

export interface Company {
  slug: CompanySlug;
  displayName: string;
  companyType: CompanyType;
  basis: CompanyBasis;
  featurePacks: CompanyFeaturePack[];
  /**
   * Practice-id annotation on the company row. Not the operational Hub link
   * table (`hub_linked_companies`); Waiting/Hub questions require that
   * separate provisioning step.
   */
  hubLink: string | null;
  isActive: boolean;
  /** ISO date. Omitted when the company has no books-start floor. */
  booksStart?: string | null;
}

/** Draft outside companies (POST asDraft / PATCH before submit). */
export interface CompanyDraft {
  slug: CompanySlug;
  displayName: string;
  companyType: CompanyType;
  basis: CompanyBasis;
  featurePacks: CompanyFeaturePack[];
  hubLink: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyCreate {
  slug: CompanySlug;
  displayName: string;
  companyType: CompanyType;
  basis: CompanyBasis;
  featurePacks: CompanyFeaturePack[];
  hubLink: string | null;
  asDraft?: boolean;
  booksStart?: string | null;
}

/** Guided onboarding status contract (OB-2, PR #1231; contracts/books-api.yaml). */
export type OnboardingCapabilityKind = "property" | "practice" | "other";

export interface OnboardingCapabilities {
  kind: OnboardingCapabilityKind;
  opsHub: boolean;
  practiceHub: boolean;
  tenantPortal: boolean;
  moov: boolean;
  trust: boolean;
  apiKey: boolean;
  statementsDownload: boolean;
}

export interface OnboardingAnswers {
  slug: CompanySlug;
  displayName: string;
  companyType: CompanyType;
  basis: CompanyBasis;
  featurePacks: CompanyFeaturePack[];
  hubLink: string | null;
  booksStart?: string | null;
}

export interface OnboardingRunCreate {
  slug: CompanySlug;
  mode: "new" | "existing";
  answers: OnboardingAnswers;
  capabilities: OnboardingCapabilities;
}

export type OnboardingStepKey =
  | "company_registered"
  | "ledger_created"
  | "protection_in_force"
  | "ledger_checks_can_run"
  | "starter_merged"
  | "ledger_checks_green"
  | "publishing_access"
  | "link_invitation"
  | "bank_linked"
  | "accounts_mapped"
  | "go_live"
  | "api_key"
  | "hub_connected"
  | "hub_properties"
  | "hub_accounts"
  | "receipt_category_map"
  | "hub_side_mapping"
  | "moov_wallet"
  | "tenant_portal"
  | "trust_accounts"
  | "statements_option"
  | "opening_balances";

export type OnboardingWho =
  | "system"
  | "am"
  | "owner"
  | "bank_linker"
  | "hub_team"
  | "portal_team";

export type OnboardingStepState = "done" | "waiting" | "blocked" | "failed";
export type OnboardingEvidence = "system_verified" | "owner_confirmed" | null;
export type OnboardingReasonCode =
  | "no_longer_true"
  | "not_found"
  | "not_allowed"
  | "check_error";

export interface OnboardingChecklistStep {
  key: OnboardingStepKey;
  title: string;
  group: string;
  required: boolean;
  state: OnboardingStepState;
  evidence: OnboardingEvidence;
  checkedAt: string | null;
  who: OnboardingWho;
  needs: "approval" | "action";
  blockedBy: OnboardingStepKey[];
  reason?: OnboardingReasonCode;
  retrySafe?: boolean;
}

export interface OnboardingProgress {
  done: number;
  total: number;
  requiredOpen: number;
}

export interface OnboardingNextStep {
  key: OnboardingStepKey;
  who: OnboardingWho;
  needs: "approval" | "action";
}

export interface OnboardingStatus {
  company: CompanySlug;
  mode: "new" | "existing";
  capabilities: OnboardingCapabilities;
  progress: OnboardingProgress;
  next: OnboardingNextStep | null;
  steps: OnboardingChecklistStep[];
}

export interface OnboardingReceipt {
  happened: string;
  unchanged: string;
  next: { who: string; text: string } | null;
}

export interface OnboardingRunResponse {
  status: OnboardingStatus;
  receipt: OnboardingReceipt;
}

export interface OnboardingDraftDeleteResponse {
  receipt: OnboardingReceipt;
}

/** Wire shapes for the onboarding agent door (contracts/books-api.yaml). */
export interface AgentIdentity {
  commonName: string;
  label: string;
  createdAt: string;
  revokedAt: string | null;
}

export interface AgentIdentityList {
  identities: AgentIdentity[];
}

export interface AgentIdentityCreate {
  commonName: string;
  label: string;
  company: CompanySlug;
}

/** The bearer is plaintext **once**, in this response only. Never store it. */
export interface AgentIdentityCreateResponse {
  identity: AgentIdentity;
  token: string;
}

export interface AgentIdentityRevokeRequest {
  commonName: string;
}

export interface AgentIdentityRevokeResponse {
  commonName: string;
  revokedAt: string;
}

export type OnboardingRevealStep = "api_key" | "link_invitation";

/** One prepared secret, shown once to the owner. Never persist or log it. */
export interface OnboardingRevealSecret {
  step: OnboardingRevealStep;
  token: string;
  company: CompanySlug;
  expiresAt: string;
  credentialId?: string;
  invitationId?: string;
  scopes?: ServiceAuthScope[];
}

export interface OnboardingRevealResponse {
  id: string;
  company: CompanySlug;
  kind: "plan" | "go_live";
  secrets: OnboardingRevealSecret[];
}

/** Read/approve shapes for the owner approval ticket (contracts/books-api.yaml). */
export type OnboardingApprovalKind = "plan" | "go_live";

export type OnboardingApprovalState =
  | "pending"
  | "approved"
  | "expired"
  | "superseded"
  | "stale";

export type OnboardingApprovalTicketStep =
  | "register"
  | "api_key"
  | "link_invitation"
  | "go_live";

export interface OnboardingApprovalPlan {
  mode: "new" | "existing";
  answers: OnboardingAnswers;
  capabilities: OnboardingCapabilities;
}

export interface OnboardingApprovalChecklistRow {
  key: OnboardingStepKey;
  state: OnboardingStepState;
}

export interface OnboardingApprovalReadResponse {
  id: string;
  company: CompanySlug;
  kind: OnboardingApprovalKind;
  state: OnboardingApprovalState;
  requestedBy: string;
  approvedBy: string | null;
  expiresAt: string | null;
  usedSteps: OnboardingApprovalTicketStep[];
  revealedSteps: OnboardingRevealStep[];
  plan: OnboardingApprovalPlan | null;
  checklist: OnboardingApprovalChecklistRow[] | null;
}

export interface OnboardingApprovalApproveResponse {
  id: string;
  company: CompanySlug;
  kind: OnboardingApprovalKind;
  approvedBy: string;
  expiresAt: string;
}

export interface CompanyUpdate {
  displayName?: string;
  companyType?: CompanyType;
  basis?: CompanyBasis;
  featurePacks?: CompanyFeaturePack[];
  hubLink?: string | null;
  isActive?: boolean;
  /**
   * Promote draft → companies when true. Portal Submit may also send `basis`
   * (and hub annotation) so promote writes the form value — not a full
   * determinations payload (owner 2026-08-11 option C). fix-r21h adds
   * quoteAssertions so Completing quotations are re-checked server-side.
   */
  submit?: boolean;
  /** Leftover — not Completing. Promote copies draft events. */
  quoteAssertions?: OpeningDocumentQuoteAssertion[];
  determinationEvent?: OpeningDeterminationPut;
  booksStart?: string | null;
}

/**
 * Provenance of the quotation attached to a Completing disposition.
 * `am_asserted` means the AM provided the quotation (paste on QB, or a
 * screenshot crop OCR'd for an unreadable tax return). Completing on an
 * extract span is `engine_extract`. The flag travels on promote;
 * the Worker derives the durable value at gate time (grant 4 / GM-1).
 */
export type QuotationProvenance = "engine_extract" | "am_asserted";

/** One quotation bound to a stored opening document (books-api.yaml). */
export interface OpeningDocumentQuoteAssertion {
  field: string;
  kind: OpeningDocumentKind;
  contentSha256Hex: string;
  filename: string;
  quote: string;
  /** Travels on promote; Worker derives the durable value at gate time. */
  provenance?: QuotationProvenance;
  citation?: { page: number; line: string };
}

export type DeterminationAction =
  | "confirm"
  | "correct"
  | "reject"
  | "no_source"
  | "engine_proposed";

export type EvidenceVia = "extract_span" | "ocr_crop" | "qb_object" | "scan_manual" | "none";

export type QuoteCheck =
  | { via: "extract_span"; hit: true; citation: { page: number; line: string } }
  | { via: "extract_span"; hit: false; reason: "not_found" | "marker" | "uncited" }
  | { via: "qb_object"; hit: true }
  | { via: "qb_object"; hit: false; reason: "not_found" | "unsearchable" }
  | { via: "ocr_artifact"; present: true }
  | { via: "none"; reason: "unsearchable" };

export function quoteCheckIsCompleting(check: QuoteCheck): boolean {
  if (check.via === "extract_span") return check.hit;
  if (check.via === "qb_object") return check.hit;
  if (check.via === "ocr_artifact") return check.present;
  return false;
}

/** GET /v1/admin/{company}/opening-determinations */
export interface OpeningDeterminationRecord {
  id: number;
  field: string;
  action: DeterminationAction;
  determinedValue: string | null;
  engineCandidate: string | null;
  quotation: string;
  citation: { page: number; line: string } | null;
  evidenceVia: EvidenceVia;
  documentKind: OpeningDocumentKind | null;
  documentContentSha256: string | null;
  ocrReadingSha256: string | null;
  matchSemanticsVersion: "beta-v2" | null;
  supersedes: number | null;
  reason: "unsearchable" | "no_opening_documents" | null;
  recordedAt: string;
  recordedBy: string;
}

export interface OpeningDeterminationPut {
  field: string;
  action: "confirm" | "correct" | "reject" | "no_source";
  determinedValue: string | null;
  engineCandidate: string | null;
  quotation: string;
  citation?: { page: number; line: string } | null;
  documentKind: OpeningDocumentKind | null;
  documentContentSha256: string | null;
  ownerAttest?: boolean;
}

export interface OpeningDeterminationsResponse {
  company: CompanySlug;
  determinations: OpeningDeterminationRecord[];
}

/** POST /v1/admin/{company}/opening-documents/verify-quote */
export interface VerifyOpeningDocumentQuoteRequest {
  kind: OpeningDocumentKind;
  contentSha256Hex: string;
  filename: string;
  quote: string;
  field?: string;
}

export type VerifyOpeningDocumentQuoteResponse = QuoteCheck;

/** GET /v1/admin/{company}/opening-documents/extract */
export type OpeningDocumentExtractStatus =
  | "ready"
  | "pending"
  | "failed"
  | "not_extractable";

export interface OpeningDocumentExtractResponse {
  company: CompanySlug;
  kind: OpeningDocumentKind;
  contentSha256Hex: string;
  documentObjectKey: string;
  status: OpeningDocumentExtractStatus;
  extract: Record<string, unknown> | null;
  failureReason: string | null;
}

/** GET /v1/admin/{company}/opening-documents/ocr — owner grant (2) 2026-08-12. */
export type OpeningOcrDeterminationField = "form" | "tax_year" | "basis";

export interface OpeningDocumentOcrResponse {
  company: CompanySlug;
  kind: OpeningDocumentKind;
  contentSha256Hex: string;
  status: "pending" | "ready" | "failed";
  text: string | null;
  failureReason: string | null;
}

/** POST /v1/admin/{company}/opening-documents/upload-url */
export interface OpeningDocumentUploadUrlRequest {
  kind: OpeningDocumentKind;
  contentSha256Hex: string;
  filename: string;
}

export interface OpeningDocumentUploadUrlResponse {
  company: CompanySlug;
  kind: OpeningDocumentKind;
  objectKey: string;
  contentSha256Hex: string;
  url: string;
  expiresAt: string;
}

/** PUT signed upload target response. */
export interface OpeningDocumentUploadResponse {
  company: CompanySlug;
  kind: OpeningDocumentKind;
  objectKey: string;
  contentSha256Hex: string;
  uploadedAt: string;
}

/**
 * Closed opening-books document kinds (pipeline/opening/vocabulary.py /
 * books-api.yaml EvidenceDocumentKind). Consumed, not restated.
 */
export type OpeningDocumentKind =
  | "bank-statement"
  | "tax-return"
  | "qb-trial-balance"
  | "qb-balance-sheet"
  | "qb-profit-and-loss"
  | "qb-general-ledger"
  | "loan-document"
  | "other";

export const OPENING_DOCUMENT_KINDS: readonly OpeningDocumentKind[] = [
  "bank-statement",
  "tax-return",
  "qb-trial-balance",
  "qb-balance-sheet",
  "qb-profit-and-loss",
  "qb-general-ledger",
  "loan-document",
  "other",
] as const;

/**
 * Document kinds that truly block opening-books mint when absent.
 * Derived from pipeline refusals on normalized report content
 * (`entries.py` requires trial_balance / balance_sheet / pnl_monthly) —
 * not from portal invention. Owner split 2026-08-10 (fix-r18b).
 *
 * Portal Completing-gate only. Worker promote does **not** enforce this
 * set (empty-inventory promote must stay reachable). A Worker twin of this
 * array was deleted in fix-r22j — production never read it (R22R-3).
 */
export const OPENING_REQUIRED_DOCUMENT_KINDS: readonly OpeningDocumentKind[] = [
  "qb-trial-balance",
  "qb-balance-sheet",
  "qb-profit-and-loss",
] as const;

/**
 * Useful for opening books but must not block Submit. Proceeding without
 * one requires an attributed acknowledgement (fix-r18b / owner 2026-08-10).
 * tax-return: pipeline raises a to-do only when present without equity
 * determinations. bank-statement: no upload-absence refusal found — cash
 * mint needs ending balances (often Plaid), not this kind by itself.
 */
export const OPENING_OPTIONAL_DOCUMENT_KINDS: readonly OpeningDocumentKind[] = [
  "tax-return",
  "bank-statement",
] as const;

/**
 * Engine-derived determination for AM confirmation (anchoring rule).
 * sourceText is always a string: a verbatim document span, or the
 * unavailable marker (`SOURCE_TEXT_UNAVAILABLE` in onboarding.ts). Empty /
 * whitespace / marker → Confirm refused at runtime (owner 2026-08-10).
 * Never reconstructed from the candidate.
 */
export interface DerivedDetermination {
  field: string;
  candidate: string;
  sourceText: string;
  /** Page + line when the extract cited this reading. */
  citation?: { page: number; line: string };
  /** Extract wire status for this tax-return (absent for QB / injected / not-yet-fetched). */
  extractStatus?:
    | "ready"
    | "pending"
    | "failed"
    | "not_extractable";
  /** Portal-only: GET extract failed — not a wire status. */
  extractLoadError?: boolean;
  /** Form-structure hint — works even when the document is unreadable. */
  locationHint?: string;
}

/**
 * AM disposition of an engine-derived determination.
 *
 * `no_source` is the honest exit when the AM looked and cannot quote a
 * verbatim span (fix-r21c / R20O-3). It is recorded as what it is and does
 * **not** complete the reading — opening equity stays blocked (owner decision
 * on this lane: block forever rather than an attested unlock that would travel
 * with promote; that travel is GM-1 and is not authorised here).
 */
export type DeterminationDecisionKind =
  | "confirm"
  | "correct"
  | "reject"
  | "no_source";

/** Server verify outcome stored on a Completing decision after Apply. */
export type QuoteVerificationStatus = "verified" | "asserted";

export type ServiceAuthScope =
  | "reports:read"
  | "deposits:read"
  | "hub:push"
  | "hub_questions:read"
  | "hub_questions:resolve"
  | "hub_balances:read"
  | "statements:read"
  | "admin:queue"
  | "admin:rules"
  | "admin:tokens"
  | "admin:close"
  | "admin:health"
  | "admin:console"
  | "admin:onboarding";

export interface TokenInfo {
  id: string;
  company: CompanySlug;
  scopes: ServiceAuthScope[];
  label?: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

export interface TokenIssueRequest {
  company: CompanySlug;
  scopes: ServiceAuthScope[];
  label?: string;
  expiresAt: string;
}

export interface TokenIssueResponse {
  id: string;
  token: string;
  company: CompanySlug;
  scopes: string[];
  label?: string | null;
  expiresAt: string;
}

export interface LinkInvitationIssueResponse {
  id: string;
  token: string;
  company: CompanySlug;
  expiresAt: string;
}

/** POST /link/session — frozen contract, snake_case on the wire. */
export interface LinkSessionResponse {
  linkToken: string;
  expiration: string;
  hostedLinkUrl: string;
}

/** POST /link/exchange — frozen contract (company + plaidItemId). */
export interface LinkExchangeResponse {
  company: CompanySlug;
  plaidItemId: string;
}

/** Staff role from GET /v1/admin/me (presentation-only; server enforces). */
export type StaffRole = "owner" | "account_manager";

export interface AdminMe {
  role: StaffRole;
  identity: string;
}

/** Shared-card list (GL-38; OpenAPI IcSplitCard / IcSplitCardChange / IcSplitCardsResponse). */
export interface IcSplitCard {
  cardLast4: string;
  companies: string[];
}

export interface IcSplitCardChange {
  id: number;
  cardLast4: string;
  changedBy: string;
  changedAt: string;
  companiesBefore: string[];
  companiesAfter: string[];
}

export interface IcSplitCardsResponse {
  splitCompanies: string[];
  cards: IcSplitCard[];
  changes: IcSplitCardChange[];
}

/** Owner dashboard group (docs/design/staff-auth-roles.md; OpenAPI DashboardGroup). */
export interface DashboardGroup {
  id: string;
  name: string;
  memberSlugs: CompanySlug[];
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardGroupCreate {
  name: string;
  memberSlugs: CompanySlug[];
  isDefault?: boolean;
}

export interface DashboardGroupUpdate {
  name?: string;
  memberSlugs?: CompanySlug[];
}

/** Addendum B — Waiting row (docs/design/console-personas.md §4.3). */
export interface AdminHubQuestionRow {
  externalRef: string;
  txnDate: string;
  amount: string;
  memo: string | null;
  assignToRole: string;
  createdAt: string;
}

export interface AdminHubQuestionsResponse {
  questions: AdminHubQuestionRow[];
  knownRoles: string[];
}

export interface HubQuestionAssignResponse {
  company: CompanySlug;
  externalRef: string;
  assignTo: { role: string };
}

// --- G11 hub intake (contracts/books-api.yaml AdminHubIntakeItem) ----------
// Hand-written mirrors: the portal never imports @gv/contracts. The enum
// values and their order match packages/contracts/src/index.ts exactly.

export const HUB_INTAKE_KINDS = ["receipt", "bill", "transfer"] as const;
export type HubIntakeKind = (typeof HUB_INTAKE_KINDS)[number];

export const HUB_INTAKE_STATES = [
  "booked",
  "waiting_on_you",
  "with_the_owner",
  "with_the_practice",
  "held",
  "not_booked_yet",
  "on_the_shelf",
  "waiting_for_the_bank_line",
  "paid_by_another_company",
  "set_aside",
] as const;
export type HubIntakeState = (typeof HUB_INTAKE_STATES)[number];

export const HUB_INTAKE_REASONS = [
  "receipt_gl_account_unmapped",
  "receipt_review_flag",
  "receipt_location_unconfigured",
  "receipt_account_not_open",
  "receipt_account_is_bank",
  "receipt_amount_date_disagrees",
  "receipt_before_books_start",
  "receipt_evidence_refused",
  "receipt_attachment_unreadable",
  "receipt_category_unreadable",
  "receipt_lookup_failed",
  "receipt_card_not_attached",
  "receipt_shared_card_outside_set",
  "receipt_needed_for_vendor",
  "receipt_not_confirmed",
  "receipt_did_not_book",
  "receipt_match_conflict",
  "receipt_match_refused",
  "receipt_match_review",
  "bill_amount_disagrees",
  "bill_line_unlocated",
  "bill_multiple_locations",
  "bill_location_not_in_ledger",
  "bill_unmapped_gl_account",
  "bill_account_not_in_chart",
  "bill_line_maps_to_bank",
  "bill_judgment_account",
  "bill_period_locked",
  "bill_match_ambiguous",
  "bill_lookup_failed",
  "wallet_card",
  "wallet_auto_booking_off",
  "transfer_needs_you",
  "other",
  "not_arrived_yet",
  "no_paid_from_account",
  "bank_line_not_found",
  "wallet_payment_not_seen",
  "bank_line_removed",
  "several_bank_lines_match",
  "no_bank_line_after_4_days",
  "answered_waiting_for_update",
  "waiting_for_next_update",
  "books_copy_not_available",
  "check_in_books",
  "paid_by_another_company",
  "not_ours",
  "duplicate",
  "card_dismissed",
] as const;
export type HubIntakeReason = (typeof HUB_INTAKE_REASONS)[number];

export type HubIntakeTransferKind =
  | "loan_to"
  | "loan_from"
  | "capital_contribution"
  | "owner_distribution"
  | "intercompany_transfer"
  | "loan_repayment";

export type HubIntakeExpenseType = "turnover" | "remodel" | "repair" | "gas" | "meals";

export type HubIntakeSubCategory =
  | "general"
  | "appliances"
  | "flooring"
  | "cabinets"
  | "hvac"
  | "plumbing"
  | "electrical";

export type HubIntakeMethod = "moov" | "eft" | "check" | "card" | "other";

export interface HubIntakeBankLine {
  source: "bank" | "moov";
  refId: string;
  date: string;
}

export interface AdminHubIntakeItem {
  kind: HubIntakeKind;
  id: number;
  receivedAt: string;
  date: string;
  amount: string;
  vendor?: string;
  transferKind?: HubIntakeTransferKind;
  property?: string;
  hubAccount?: string;
  expenseType?: HubIntakeExpenseType;
  subCategory?: HubIntakeSubCategory;
  lineCount?: number;
  method?: HubIntakeMethod;
  cardLast4?: string;
  state: HubIntakeState;
  reason?: HubIntakeReason;
  bankLine?: HubIntakeBankLine;
  booking?: ActivityBooking;
  queueId?: number;
}

export interface AdminHubIntakeCounts {
  booked: number;
  waiting_on_you: number;
  with_the_owner: number;
  with_the_practice: number;
  held: number;
  not_booked_yet: number;
  on_the_shelf: number;
  waiting_for_the_bank_line: number;
  paid_by_another_company: number;
  set_aside: number;
}

export interface AdminHubIntakeResponse {
  company: CompanySlug;
  period: string;
  generatedAt: string;
  hubSetup: "receives" | "not_set_up" | "unknown";
  ledger: {
    available: boolean;
    asOf?: string;
  };
  counts: AdminHubIntakeCounts;
  countsComplete: boolean;
  total: number;
  capped: boolean;
  items: AdminHubIntakeItem[];
}

export interface HubIntakeQuery {
  period?: string;
  state?: HubIntakeState;
  kind?: HubIntakeKind;
  limit?: number;
}

/** Admin statement upload response (docs/design/statement-upload-seam.md + recon). */
export interface StatementUploadResponse {
  company: CompanySlug;
  period: string;
  account: AccountPath;
  contentSha256Hex: string;
  uploadedAt: string;
}

/**
 * One file attached to a transaction (owner decision 8, 2026-11-02; owner GRANT
 * G15). A private copy of the document the line came from — Books never reads,
 * OCRs or categorises it, and it never changes a booking.
 *
 * `filename` is untrusted upload text the Worker already sanitised (line
 * hazards replaced, last path segment only, ≤200 characters) and it is still
 * rendered through the shared strip. `uploadedBy` is `staff_roles.identity_key`:
 * an email address, or an opaque sign-in subject, which `face.person` decides
 * whether a person can read.
 */
export type TransactionAttachmentContentType =
  | "application/pdf"
  | "image/jpeg"
  | "image/png"
  | "image/heic"
  | "image/heif";

/** Which console line an attachment belongs to: a bank line or a wallet payment. */
export type TransactionAttachmentSource = "bank" | "moov";

export interface TransactionAttachment {
  id: string;
  filename: string;
  contentType: TransactionAttachmentContentType;
  byteSize: number;
  uploadedBy: string;
  uploadedAt: string;
}

export interface TransactionAttachmentListResponse {
  company: CompanySlug;
  source: TransactionAttachmentSource;
  refId: string;
  attachments: TransactionAttachment[];
}

export interface TransactionAttachmentUploadResponse {
  company: CompanySlug;
  source: TransactionAttachmentSource;
  refId: string;
  attachment: TransactionAttachment;
}

export interface TransactionAttachmentUrlResponse {
  company: CompanySlug;
  attachmentId: string;
  url: string;
  expiresAt: string;
}

/** Statement reconciliation row for AM Statements confirm cards (R2). */
export type PublicationState =
  | "calculated"
  | "awaiting_publication"
  | "published"
  | "stale"
  | "blocked";

export interface StatementReconRow {
  id: string;
  company: CompanySlug;
  period: string;
  account: AccountPath;
  source: "plaid" | "statement" | "both";
  plaidBalanceDecimal: string | null;
  statementBalanceDecimal: string | null;
  outcome: "auto_confirmed" | "mismatch" | "needs_review";
  closingDate: string;
  updatedAt: string;
  publicationState: PublicationState;
  /**
   * KPB23. The statement fingerprint this reconciliation is bound to — the
   * value the confirmation must send back. `null` means the Worker says there
   * is no binding yet: every upload nulls it and sets needs_review until the
   * statements workflow restamps it, so the Statements page shows no form and
   * keeps polling. A null binding alone is not proof a check is running
   * (Plaid-only rows stay null forever), so the page says a statement is being
   * checked only with evidence — a statement this session uploaded for that
   * account and month, or the Worker answering `not_bound` (rd Z8 round 3).
   *
   * Optional in this hand-written mirror (the contract makes it required) so a
   * row literal written before rd Z8 still compiles. A missing or empty value
   * is treated exactly like `null` (rd Z8 round 2, MUST-FIX 3): no form, no
   * press, and never an empty fingerprint sent to the Worker.
   */
  contentSha256Hex?: string | null;
}

export interface StatementConfirmRequest {
  accountMask: string;
  markerPattern: string;
  balanceCueLabel: string;
  confirmedBalanceDecimal: string;
  /** KPB23. The fingerprint from the reconciliation row, never a client guess. */
  contentSha256Hex: string;
}

export interface StatementConfirmResponse {
  company: CompanySlug;
  period: string;
  account: AccountPath;
  outcome: "auto_confirmed";
  fingerprintVersion: number;
}

/** Schema default for hub-question assignTo — frozen `hubQuestionAssignToSchema` / seam default. */
export const DEFAULT_ASSIGN_ROLE = "practice-manager";

/** Statement PDF size bound — mirrors `ingest-worker/src/statements.ts` MAX_STATEMENT_PDF_BYTES. */
export const MAX_STATEMENT_PDF_BYTES = 10 * 1024 * 1024;

/** Owner Audit screen. Wire fields that the frozen schema names stay snake_case. */
export interface ExpenseAuditLeg {
  company: CompanySlug;
  location: string;
  account: AccountPath;
  amount: string;
}

export type ExpenseAuditPairState =
  | "none"
  | "awaiting_counterpart"
  | "paired"
  | "failed_counterpart";

export interface ExpenseAuditDecisionView {
  id: string;
  status: "pending_apply" | "applied" | "conflict" | "failed" | "cancelled";
  statusReason: string | null;
  newLegs: ExpenseAuditLeg[];
  pairState: ExpenseAuditPairState;
  icPairId: string | null;
  /** Who made the decision (the Worker's `origin`, e.g. "owner_audit"). */
  origin: string | null;
}

export interface ExpenseAuditReceiptFacts {
  vendor: string;
  expenseType: string;
  subCategory: string | null;
  /** Hub account name, present only for expenseType "gl_account". Untrusted. */
  glAccount?: string;
  /** Hub parent account name, context only. Untrusted. */
  glParent?: string;
  capitalReplacement: boolean | null;
  note: string | null;
  property: string;
  address: string | null;
  submittedBy: string;
  imageUrl: string | null;
}

export interface ExpenseAuditBillLine {
  property: string;
  unit: string | null;
  hubAccount: string;
  description: string;
  amount: string;
  paidBy: string | null;
}

export interface ExpenseAuditDocumentRef {
  documentId: string;
  contentType: string;
  seq: number;
  declaredCount: number;
}

export type ExpenseAuditReviewState = "not_reviewed" | "reviewed" | "changed";

export type ExpenseAuditSource = "receipt" | "invoice" | "no_document";

/**
 * One audit card. Field names follow design §11.1. The items array in
 * `books-api.yaml` `ExpenseAuditItemsPage` does not name these fields;
 * this is the shape the screen reads (snake_case on the wire, parsed here).
 */
export interface ExpenseAuditItem {
  targetKind: "plaid" | "moov";
  targetId: string;
  date: string;
  /** The ledger period the Worker's entry belongs to (`2026-09`). */
  period: string;
  /** The payer's name as the Worker stores it; null when the bank sent none. */
  payee: string | null;
  narration: string | null;
  bankAmount: string;
  /**
   * The Worker's entry's cash posting account (its `booking_account`). It is
   * the bank side, not an expense: held for a later screen, never printed as
   * "Booked as".
   */
  bookingAccount: string | null;
  accountMask: string | null;
  originalSource: string | null;
  currentLegs: ExpenseAuditLeg[];
  legsSha256: string;
  periodLocked: boolean;
  reviewState: ExpenseAuditReviewState;
  latestDecision: ExpenseAuditDecisionView | null;
  receipt: ExpenseAuditReceiptFacts | null;
  billLines: ExpenseAuditBillLine[];
  documents: ExpenseAuditDocumentRef[];
  source: ExpenseAuditSource;
}

export interface ExpenseAuditItemsPage {
  items: ExpenseAuditItem[];
  nextAfter: string | null;
}

export interface ExpenseAuditDecisionBody {
  expected_legs_sha256: string;
  lines: ExpenseAuditLeg[];
  note?: string;
  replaces_decision_id?: string;
}

export interface ExpenseAuditDecisionAccepted {
  decisionId: string;
  icPairId: string | null;
  status: "pending_apply";
}

export interface ExpenseAuditSignedUrl {
  documentId: string;
  url: string;
  contentType: string;
  expiresAt: string;
}

export interface ExpenseAuditViewRecord {
  id: string;
  name: string;
  accountSuffixes: string[];
  quickPicks: string[];
}

export type RouteName =
  | "errors"
  | "home"
  | "reports"
  | "tax-time"
  | "intercompany"
  | "general-ledger"
  | "months"
  | "transactions"
  | "balances"
  | "queue"
  | "waiting"
  | "statements"
  | "receipt-shelf"
  | "recurring"
  | "attached-payments"
  | "groups"
  | "cards"
  | "rules"
  | "tokens"
  | "invitations"
  | "health"
  | "onboarding"
  | "add-company"
  | "company-checklist"
  | "prior-import"
  | "compare"
  | "expense-audit"
  | "agent-access"
  | "approval";

export type IntercompanyPairStatus = "pass" | "fail" | "unmatched";

export interface IntercompanyPair {
  receivableSegment: string;
  payableSegment: string;
  receivableCompany?: string | null;
  payableCompany?: string | null;
  dueFromAccount: string;
  dueToAccount: string;
  dueFromBalance?: string | null;
  dueToBalance?: string | null;
  net?: string | null;
  status: IntercompanyPairStatus;
  currency: string;
}

export interface IntercompanyCompany {
  slug: CompanySlug;
  segment: string;
  status: "checked" | "n/a";
}

export interface IntercompanySnapshot {
  generatedAt: string | null;
  allPassed: boolean;
  pairCount: number;
  companies: IntercompanyCompany[];
  pairs: IntercompanyPair[];
}

export type RecurringKind = "recurring" | "loan";
export type RecurringRunStatus = "pending" | "succeeded" | "failed";

export interface RecurringScheduleItem {
  kind: RecurringKind;
  name: string;
  enabled: boolean;
  day: number | null;
  narration: string;
  location: string;
  postingSummary: string;
}

export interface RecurringRunRequestView {
  id: string;
  company: CompanySlug;
  period: string;
  status: RecurringRunStatus;
  requestedAt: string;
  entriesWritten: number | null;
  entriesSkipped: number | null;
  errorCode: string | null;
  completedAt: string | null;
}

export interface RecurringAdminGet {
  company: CompanySlug;
  present: boolean;
  schedule: RecurringScheduleItem[];
  requests: RecurringRunRequestView[];
}

export interface RecurringRunRequestResult {
  id: string;
  company: CompanySlug;
  period: string;
  status: RecurringRunStatus;
}

// Check now (lane feat-check-now): shaped to CheckNowStatus in
// contracts/books-api.yaml. Counts, states, times and a run link only.
export type CheckNowPressStatus = "running" | "done" | "failed";

export interface CheckNowReads {
  moov: {
    state: "not_configured" | "read" | "partly_read" | "failed";
    walletsRead: number;
    walletsSkipped: number;
    newMovements: number;
    refreshedMovements: number;
    mayHaveMore: boolean;
  };
  plaid: {
    state: "not_connected" | "read" | "partly_read" | "failed";
    itemsRead: number;
    itemsFailed: number;
    itemsNeedReconnect: number;
    newOrChangedLines: number;
  };
}

export interface CheckNowSync {
  state: "started" | "skipped_run_in_progress" | "not_started";
  startedAt: string | null;
  runUrl: string | null;
}

export interface CheckNowOutcome {
  state:
    | "reading"
    | "booking"
    | "booked"
    | "nothing_new"
    | "could_not_finish"
    | "not_seen";
  newlyBooked: number | null;
  newForReview: number | null;
  errorCode: string | null;
}

export interface CheckNowPress {
  pressId: string;
  requestedAt: string;
  completedAt: string | null;
  status: CheckNowPressStatus;
  reads: CheckNowReads | null;
  sync: CheckNowSync | null;
  outcome: CheckNowOutcome;
}

export interface CheckNowStatus {
  company: CompanySlug;
  cooldownSeconds: number;
  nextAllowedAt: string | null;
  press: CheckNowPress | null;
}

export type VendorIdentityClassification = "1099_contractor" | "non_1099_vendor";

export interface VendorIdentitySnapshotItem {
  vendorId: string;
  matchText: string;
  classification: VendorIdentityClassification | null;
  active: boolean;
}

export interface VendorIdentitySnapshotGet {
  company: CompanySlug;
  present: boolean;
  identities: VendorIdentitySnapshotItem[];
}

/** Unmatched-receipt shelf wire shapes (OpenAPI grant pending). */
export type ReceiptShelfArchive = "not_ours" | "duplicate";

export interface ReceiptShelfItem {
  id: number;
  company: CompanySlug;
  vendor: string;
  receiptDate: string;
  amountDecimal: string;
  currency: string;
  cardLast4?: string | null;
  cardBrand?: string | null;
  location: string;
  property: string;
  sorAddress?: string | null;
  expenseType: string;
  subCategory?: string | null;
  /** Hub account name, present only for expenseType "gl_account". Untrusted. */
  glAccount?: string;
  /** Hub parent account name, context only. Untrusted. */
  glParent?: string;
  submittedBy: string;
  submittedByEmail: string;
  imageUrl: string;
  matchState: string;
  reviewState: string;
  dismissedArchive?: ReceiptShelfArchive | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  receivedAt: string;
  updatedAt: string;
  factsLine?: string;
}

export interface ReceiptShelfCandidate {
  plaidTransactionId: string;
  txnDate: string;
  amountDecimal: string;
  accountLast4?: string | null;
  name?: string | null;
  merchantName?: string | null;
  alreadyHasReceipt: boolean;
}

/** GET /v1/admin/{company}/queue/{id}/receipt-match-correction */
export interface ReceiptMatchCorrectionReceipt {
  id: number;
  vendor: string;
  receiptDate: string;
  amountDecimal: string;
  cardLast4: string | null;
}

export interface ReceiptMatchCorrectionPage {
  receipt: ReceiptMatchCorrectionReceipt;
  proposedPlaidTransactionId: string;
  candidates: ReceiptShelfCandidate[];
  preselectedPlaidTransactionId: null;
}

export interface ReceiptShelfCardPropertyWarning {
  kind: "card_property_mismatch";
  message: string;
  cardCompany: string;
  propertyCompany: string;
  intercompany: { dueFromAccount: AccountPath; dueToAccount: AccountPath };
}

export interface ReceiptShelfAlreadyHasWarning {
  kind: "already_has_receipt";
  message: string;
  incumbentReceiptId: number;
}

export type ReceiptShelfWarning =
  | ReceiptShelfCardPropertyWarning
  | ReceiptShelfAlreadyHasWarning;

export interface AdminConfig {
  apiBase: string;
  token: string;
  operatorId: string;
}
